import { supabase } from "./supabase";

/**
 * Modal Settings Service
 *
 * Manages system-wide modal settings including:
 * - Notice modal enable/disable
 * - Modal content (title, message, etc.)
 * - Display frequency settings
 * - Target audience settings
 */

const MODAL_SETTINGS_TABLE = "modal_settings";
const DEFAULT_SETTINGS = {
  notice_modal_enabled: true,
  notice_modal_title: "Quick Update",
  notice_modal_date: "August 16, 2025",
  notice_modal_content: {
    intro:
      "We had a minor hiccup with our Facebook integration yesterday that temporarily affected some ad accounts. No big deal - we're sorting it out and expect everything to be back to normal as early as tomorrow!",
    main: "We've got a ton of improvements and optimizations rolling out soon that will unlock higher ad spend limits and boost earnings for everyone. This brief pause actually gives us a perfect window to implement some of these upgrades.",
    closing:
      "Thanks for your patience - bigger and better things are coming very soon!",
  },
  notice_modal_show_interval_hours: 6,
  notice_modal_target_audience: "affiliates", // "affiliates", "all", "none"
  // Separate content for Bank Accounts notice
  bank_notice_enabled: true,
  bank_notice_title: "Banking Update",
  bank_notice_date: null,
  bank_notice_content: {
    intro: "Please ensure your banking details are accurate before submission.",
    main: "We may take up to 1-2 business days to verify new bank accounts.",
    closing: "If you need help, contact support from your dashboard chat.",
  },
  bank_notice_show_interval_hours: 6,
  created_at: new Date().toISOString(),
  updated_at: new Date().toISOString(),
};

export const modalSettingsService = {
  // Get current modal settings
  getSettings: async () => {
    try {
      console.log("🔧 Loading modal settings...");

      const { data, error } = await supabase
        .from(MODAL_SETTINGS_TABLE)
        .select("*")
        .order("updated_at", { ascending: false })
        .limit(1)
        .single();

      if (error && error.code !== "PGRST116") {
        // PGRST116 = no rows returned
        console.error("❌ Error loading modal settings:", error);
        throw new Error(`Failed to load modal settings: ${error.message}`);
      }

      // If no settings exist, return defaults
      if (!data) {
        console.log("📝 No modal settings found, using defaults");
        return DEFAULT_SETTINGS;
      }

      console.log("✅ Modal settings loaded:", data);
      return data;
    } catch (error) {
      console.error("❌ Modal settings service error:", error);
      throw error;
    }
  },

  // Update modal settings
  updateSettings: async (settings) => {
    try {
      console.log("💾 Updating modal settings:", settings);

      const updatedSettings = {
        ...settings,
        updated_at: new Date().toISOString(),
      };

      // First try to get existing record
      const { data: existing } = await supabase
        .from(MODAL_SETTINGS_TABLE)
        .select("id")
        .order("updated_at", { ascending: false })
        .limit(1)
        .single();

      let result;
      if (existing) {
        // Update existing record
        result = await supabase
          .from(MODAL_SETTINGS_TABLE)
          .update(updatedSettings)
          .eq("id", existing.id)
          .select()
          .single();
      } else {
        // Insert new record
        result = await supabase
          .from(MODAL_SETTINGS_TABLE)
          .insert([
            {
              ...updatedSettings,
              created_at: new Date().toISOString(),
            },
          ])
          .select()
          .single();
      }

      if (result.error) {
        console.error("❌ Error updating modal settings:", result.error);
        throw new Error(
          `Failed to update modal settings: ${result.error.message}`
        );
      }

      console.log("✅ Modal settings updated successfully:", result.data);
      return result.data;
    } catch (error) {
      console.error("❌ Modal settings update error:", error);
      throw error;
    }
  },

  // Toggle notice modal on/off
  toggleNoticeModal: async (enabled) => {
    try {
      const currentSettings = await modalSettingsService.getSettings();
      return await modalSettingsService.updateSettings({
        ...currentSettings,
        notice_modal_enabled: enabled,
      });
    } catch (error) {
      console.error("❌ Error toggling notice modal:", error);
      throw error;
    }
  },

  // Update notice modal content
  updateNoticeContent: async (title, date, content) => {
    try {
      const currentSettings = await modalSettingsService.getSettings();
      return await modalSettingsService.updateSettings({
        ...currentSettings,
        notice_modal_title: title,
        notice_modal_date: date,
        notice_modal_content: content,
      });
    } catch (error) {
      console.error("❌ Error updating notice content:", error);
      throw error;
    }
  },

  // Update display frequency
  updateDisplayFrequency: async (hours) => {
    try {
      const currentSettings = await modalSettingsService.getSettings();
      return await modalSettingsService.updateSettings({
        ...currentSettings,
        notice_modal_show_interval_hours: hours,
      });
    } catch (error) {
      console.error("❌ Error updating display frequency:", error);
      throw error;
    }
  },

  // Reset to defaults
  resetToDefaults: async () => {
    try {
      console.log("🔄 Resetting modal settings to defaults...");
      return await modalSettingsService.updateSettings(DEFAULT_SETTINGS);
    } catch (error) {
      console.error("❌ Error resetting to defaults:", error);
      throw error;
    }
  },

  // Get settings for client-side use (without sensitive data)
  getClientSettings: async () => {
    try {
      console.log("🔍 Getting client settings...");
      const settings = await modalSettingsService.getSettings();
      console.log("📋 Client settings retrieved:", settings);

      // Return only settings needed by client
      const clientSettings = {
        notice_modal_enabled: settings.notice_modal_enabled,
        notice_modal_title: settings.notice_modal_title,
        notice_modal_date: settings.notice_modal_date,
        notice_modal_content: settings.notice_modal_content,
        notice_modal_show_interval_hours:
          settings.notice_modal_show_interval_hours,
        notice_modal_target_audience: settings.notice_modal_target_audience,
        // Bank notice fields
        bank_notice_enabled: settings.bank_notice_enabled ?? true,
        bank_notice_title: settings.bank_notice_title ?? "Banking Update",
        bank_notice_date: settings.bank_notice_date ?? null,
        bank_notice_content:
          settings.bank_notice_content ?? DEFAULT_SETTINGS.bank_notice_content,
        bank_notice_show_interval_hours:
          settings.bank_notice_show_interval_hours ?? 6,
      };

      console.log("✅ Returning client settings:", clientSettings);
      return clientSettings;
    } catch (error) {
      console.error("❌ Error getting client settings:", error);
      console.log("🔄 Returning fallback defaults due to error");
      // Return safe defaults on error
      return {
        notice_modal_enabled: true, // Changed to true for testing
        notice_modal_title: "System Update",
        notice_modal_date: new Date().toLocaleDateString(),
        notice_modal_content: DEFAULT_SETTINGS.notice_modal_content,
        notice_modal_show_interval_hours: 6,
        notice_modal_target_audience: "affiliates",
        // Fallback bank notice
        bank_notice_enabled: true,
        bank_notice_title: "Banking Update",
        bank_notice_date: new Date().toLocaleDateString(),
        bank_notice_content: DEFAULT_SETTINGS.bank_notice_content,
        bank_notice_show_interval_hours: 6,
      };
    }
  },

  // Debug function to check table structure
  debugTableStructure: async () => {
    try {
      const { data, error } = await supabase
        .from(MODAL_SETTINGS_TABLE)
        .select("*")
        .limit(1);

      if (error) {
        console.error("❌ Table structure check failed:", error);
        return { error: error.message };
      }

      console.log("🔍 Modal settings table structure:", {
        hasData: data && data.length > 0,
        sampleData: data?.[0] || null,
        columns: data?.[0] ? Object.keys(data[0]) : [],
      });

      return {
        hasData: data && data.length > 0,
        sampleData: data?.[0] || null,
        columns: data?.[0] ? Object.keys(data[0]) : [],
      };
    } catch (error) {
      console.error("❌ Debug table structure error:", error);
      return { error: error.message };
    }
  },
};

// Make debug functions available globally
if (typeof window !== "undefined") {
  window.modalSettingsDebug = {
    getSettings: modalSettingsService.getSettings,
    debugTable: modalSettingsService.debugTableStructure,
    reset: modalSettingsService.resetToDefaults,
  };
}

export default modalSettingsService;
